import React from 'react';
import { AlertActionProps, AlertProps } from './alert.types';
import {
  AlertCircle,
  ChevronRight,
  Info,
  TriangleAlert
} from 'lucide-react';
import {
  alertActionOutlinedClass,
  alertActionVariants,
  alertOutlinedBorderClass,
  alertVariants
} from './alert.variants';
import { cn } from '@/lib/utils';

const variantIcon: Record<NonNullable<AlertProps['variant']>, React.ComponentType<{ className?: string }>> = {
  attention: TriangleAlert,
  default: Info,
  destructive: AlertCircle,
  important: AlertCircle,
  info: Info
};

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  (
    {
      children,
      className,
      outlined = false,
      variant = 'default',
      withIcon = false,
      ...props
    },
    ref
  ) => {
    const Icon = withIcon ? variantIcon[variant ?? 'default'] : null;
    const v = variant ?? 'default';

    return (
      <div
        className={cn(
          alertVariants({ variant }),
          outlined && alertOutlinedBorderClass[v],
          className
        )}
        ref={ref}
        role="alert"
        {...props}
      >
        {Icon ? <Icon aria-hidden /> : null}
        {children}
      </div>
    );
  }
);
Alert.displayName = 'Alert';

const AlertTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  (
    {
      className,
      ...props
    },
    ref
  ) => (
    <h5
      className={cn(
        'mb-0.5 font-sans text-sm font-semibold leading-5 tracking-normal',
        className
      )}
      ref={ref}
      {...props}
    />
  )
);
AlertTitle.displayName = 'AlertTitle';

const AlertDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  (
    {
      className,
      ...props
    },
    ref
  ) => (
    <div
      className={cn('font-sans text-xs leading-4 text-muted-foreground [&_p]:leading-5', className)}
      ref={ref}
      {...props}
    />
  )
);
AlertDescription.displayName = 'AlertDescription';

const alertActionDefaultIcon: Record<
  NonNullable<AlertActionProps['variant']>,
  React.ComponentType<{ className?: string }>
> = {
  attention: TriangleAlert,
  important: AlertCircle,
  neutral: Info
};

const iconToneClass: Record<NonNullable<AlertActionProps['variant']>, string> = {
  attention: 'text-[hsl(var(--warning))]',
  important: 'text-[hsl(var(--destructive))]',
  neutral: 'text-muted-foreground'
};

const AlertAction = React.forwardRef<HTMLButtonElement, AlertActionProps>(
  (
    {
      className,
      hideChevron,
      hint,
      hintClassName,
      icon,
      label,
      outlined = false,
      variant = 'neutral',
      ...props
    },
    ref
  ) => {
    const v = variant ?? 'neutral';
    const DefaultIcon = alertActionDefaultIcon[v];

    return (
      <button
        className={cn(
          alertActionVariants({ variant }),
          outlined && alertActionOutlinedClass[v],
          outlined && v !== 'neutral' && 'px-[16px]',
          className
        )}
        ref={ref}
        type="button"
        {...props}
      >
        <span className="flex min-w-0 flex-1 items-start gap-2">
          <span
            aria-hidden
            className={cn(
              'mt-0.5 flex size-5 shrink-0 items-center justify-center',
              iconToneClass[v]
            )}
          >
            {icon ?? <DefaultIcon className="size-5" />}
          </span>
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="truncate font-sans text-sm leading-5 text-foreground">{label}</span>
            {hint ? (
              <span
                className={cn(
                  'font-sans text-xs leading-4 text-muted-foreground',
                  !hintClassName && 'truncate',
                  hintClassName
                )}
              >
                {hint}
              </span>
            ) : null}
          </span>
        </span>
        {hideChevron ? null : (
          <ChevronRight
            aria-hidden
            className="size-4 shrink-0 text-muted-foreground-soft"
          />
        )}
      </button>
    );
  }
);
AlertAction.displayName = 'AlertAction';

export {
  Alert,
  AlertAction,
  AlertDescription,
  AlertTitle
};